<template>
  <el-dialog
    :title="form.id ? '编辑标签' : '新建标签'"
    :visible="visible"
    width="620px"
    :close-on-click-modal="false"
    append-to-body
    @update:visible="$emit('update:visible', $event)"
    @closed="handleClosed"
  >
    <el-form ref="form" :model="form" :rules="rules" label-position="top">
      <div class="two-column-form">
        <el-form-item label="所属标签组" prop="group_id">
          <el-select
            v-model="form.group_id"
            filterable
            style="width: 100%"
            @change="form.parent_id = null"
          >
            <el-option
              v-for="group in groups"
              :key="group.id"
              :label="group.name"
              :value="group.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="上级标签（可选）">
          <el-select
            v-model="form.parent_id"
            clearable
            filterable
            style="width: 100%"
            placeholder="没有上级"
          >
            <el-option
              v-for="tagOption in parentTagOptions"
              :key="tagOption.id"
              :label="tagOption.name"
              :value="tagOption.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </div>

      <el-form-item label="标签名称" prop="name">
        <el-input
          v-model.trim="form.name"
          maxlength="100"
          show-word-limit
          placeholder="例如：赵本山、PK、伤感"
        ></el-input>
        <div class="field-help">名称要简短、明确，同一标签组内不能重名。</div>
      </el-form-item>

      <el-form-item label="标签拼音">
        <el-input
          v-model.trim="form.pinyin"
          maxlength="64"
          placeholder="例如 zhouxingchi"
        ></el-input>
        <div class="field-help">全拼拼音，支持拼音搜索；留空时可用脚本自动生成，也可人工修正。</div>
      </el-form-item>

      <el-form-item label="拼音首字母">
        <el-input
          v-model.trim="form.pinyin_abbr"
          maxlength="64"
          placeholder="例如 zxc"
        ></el-input>
        <div class="field-help">每个字拼音的首字母，例如“周星驰”为 zxc。</div>
      </el-form-item>

      <el-form-item label="标签别名">
        <el-select
          v-model="form.aliases"
          multiple
          filterable
          allow-create
          default-first-option
          style="width: 100%"
          placeholder="输入一个别名后按回车，可连续添加"
        >
          <el-option
            v-for="alias in form.aliases"
            :key="alias"
            :label="alias"
            :value="alias"
          ></el-option>
        </el-select>
        <div class="field-help">
          别名是同一概念的其他叫法，例如“周星驰”的别名可以是“星爷”。
        </div>
      </el-form-item>

      <el-form-item label="说明">
        <el-input
          v-model.trim="form.description"
          type="textarea"
          :rows="2"
          maxlength="255"
          show-word-limit
          placeholder="可填写适用范围，避免以后误用"
        ></el-input>
      </el-form-item>

      <el-divider content-position="left">客户端语义与默认图标</el-divider>
      <el-alert
        type="info"
        :closable="false"
        show-icon
        title="普通标签无需填写。只有需要稳定驱动客户端默认图标的标签才设置语义编码。"
      ></el-alert>

      <div class="two-column-form semantic-fields">
        <el-form-item label="稳定语义编码">
          <el-input
            v-model.trim="form.semantic_code"
            maxlength="64"
            placeholder="例如 light_music"
            @input="handleSemanticCodeInput"
          ></el-input>
          <div class="field-help">只能使用小写字母、数字和下划线，必须以字母开头；保存后不要随意改名。</div>
        </el-form-item>
        <el-form-item label="默认图标优先级">
          <el-input-number
            v-model="form.icon_priority"
            :disabled="!form.semantic_code"
            :min="0"
            :max="999999"
          ></el-input-number>
          <div class="field-help">一个音效命中多个语义标签时，数值越大越优先。</div>
        </el-form-item>
      </div>

      <el-form-item label="默认图标">
        <ImageAssetSelect
          v-model="form.icon_image_id"
          :selected="selectedIconAsset"
          :disabled="!form.semantic_code"
          @change="selectedIconAsset = $event"
        ></ImageAssetSelect>
        <div class="field-help">图标来自公共图片资源库；留空时客户端继续使用通用默认图标。</div>
      </el-form-item>

      <div class="three-column-form">
        <el-form-item label="显示顺序">
          <el-input-number v-model="form.sort" :min="0" :max="999999"></el-input-number>
        </el-form-item>
        <el-form-item label="可用于搜索">
          <el-switch
            v-model="form.is_searchable"
            :active-value="1"
            :inactive-value="0"
          ></el-switch>
        </el-form-item>
        <el-form-item label="可用于筛选">
          <el-switch
            v-model="form.is_filterable"
            :active-value="1"
            :inactive-value="0"
          ></el-switch>
        </el-form-item>
      </div>

      <el-form-item label="标签状态">
        <el-radio-group v-model="form.status">
          <el-radio-button :label="1">启用</el-radio-button>
          <el-radio-button :label="0">停用</el-radio-button>
        </el-radio-group>
        <div class="field-help">不再使用但已有音效关联的标签，建议停用而不是删除。</div>
      </el-form-item>
    </el-form>

    <span slot="footer">
      <el-button :disabled="saving" @click="$emit('update:visible', false)">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存标签</el-button>
    </span>
  </el-dialog>
</template>

<script>
import apis from '@/api/index'
import ImageAssetSelect from '@/components/assets/ImageAssetSelect.vue'

/**
 * 创建通用标签弹窗的空表单。
 *
 * @param {number|null} groupId 从调用位置带入的标签组
 * @param {string} name 从组内搜索框带入的候选标签名
 * @returns {Object}
 */
const createEmptyForm = (groupId = null, name = '') => ({
  id: 0,
  group_id: groupId,
  parent_id: null,
  name,
  pinyin: '',
  pinyin_abbr: '',
  aliases: [],
  description: '',
  sort: 0,
  status: 1,
  is_searchable: 1,
  is_filterable: 1,
  semantic_code: '',
  icon_image_id: null,
  icon_priority: 0
})

export default {
  name: 'SoundTagDialog',
  components: {
    ImageAssetSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      default: () => []
    },
    tag: {
      type: Object,
      default: null
    },
    defaultGroupId: {
      type: [Number, String],
      default: null
    },
    defaultName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      saving: false,
      form: createEmptyForm(),
      selectedIconAsset: null,
      rules: {
        group_id: [{ required: true, message: '请选择标签组', trigger: 'change' }],
        name: [{ required: true, message: '请输入标签名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    /**
     * 返回当前标签组中可选择的父标签，并排除正在编辑的标签自身。
     *
     * @returns {Array}
     */
    parentTagOptions() {
      const group = this.groups.find(item => Number(item.id) === Number(this.form.group_id))
      if (!group) return []
      return (group.tags || []).filter(item => Number(item.id) !== Number(this.form.id))
    }
  },
  watch: {
    /**
     * 弹窗每次打开时重新构造表单，避免上一次编辑草稿残留。
     *
     * @param {boolean} value 是否显示
     * @returns {void}
     */
    visible(value) {
      if (value) this.resetForm()
    }
  },
  methods: {
    /**
     * 根据“编辑已有标签”或“快速新建标签”场景初始化表单。
     *
     * @returns {void}
     */
    resetForm() {
      this.selectedIconAsset = this.tag && this.tag.icon_asset
        ? { ...this.tag.icon_asset }
        : null
      this.form = this.tag
        ? {
            id: this.tag.id,
            group_id: this.tag.group_id,
            parent_id: this.tag.parent_id || null,
            name: this.tag.name,
            pinyin: this.tag.pinyin || '',
            pinyin_abbr: this.tag.pinyin_abbr || '',
            aliases: [...(this.tag.alias_names || [])],
            description: this.tag.description || '',
            sort: Number(this.tag.sort || 0),
            status: Number(this.tag.status),
            is_searchable: Number(this.tag.is_searchable),
            is_filterable: Number(this.tag.is_filterable),
            semantic_code: this.tag.semantic_code || '',
            icon_image_id: this.tag.icon_image_id ? Number(this.tag.icon_image_id) : null,
            icon_priority: Number(this.tag.icon_priority || 0)
          }
        : createEmptyForm(
            this.defaultGroupId ? Number(this.defaultGroupId) : null,
            this.defaultName.trim()
          )
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    /**
     * 弹窗关闭后复位保存状态，防止下次打开按钮仍显示加载中。
     *
     * @returns {void}
     */
    handleClosed() {
      this.saving = false
      this.selectedIconAsset = null
    },
    handleSemanticCodeInput(value) {
      const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9_]/g, '')
      if (normalized !== value) this.form.semantic_code = normalized
      if (!normalized) {
        this.form.icon_image_id = null
        this.form.icon_priority = 0
        this.selectedIconAsset = null
      }
    },
    /**
     * 校验并保存标签，成功后把新标签回传给调用编辑器。
     *
     * @returns {void}
     */
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            ...this.form,
            semantic_code: String(this.form.semantic_code || '').toLowerCase(),
            icon_image_id: this.form.semantic_code ? this.form.icon_image_id : null,
            icon_priority: this.form.semantic_code ? Number(this.form.icon_priority || 0) : 0
          }
          const response = await apis.tag.saveTag(payload)
          if (response.data.code !== 1) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
          const savedTag = {
            ...payload,
            id: Number((response.data.data || {}).id),
            icon_asset: this.selectedIconAsset
          }
          this.$message.success(this.form.id ? '标签修改已保存' : '新标签已创建')
          this.$emit('saved', savedTag)
          this.$emit('update:visible', false)
        } catch (error) {
          this.$message.error(String(error.message || error || '保存失败'))
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.two-column-form,
.three-column-form {
  display: grid;
  gap: 16px;
}

.two-column-form {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.three-column-form {
  grid-template-columns: 1.2fr 1fr 1fr;
}

.field-help {
  margin-top: 5px;
  color: #8a96a8;
  font-size: 12px;
  line-height: 1.6;
}

.semantic-fields {
  margin-top: 14px;
}

@media (max-width: 680px) {
  .two-column-form,
  .three-column-form {
    grid-template-columns: 1fr;
  }
}
</style>
